import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Card, Row, Col, Badge, Alert, Spinner, Table } from "react-bootstrap";
import "../../assets/css/Details.css";
import { FaEdit, FaTrashAlt, FaPlus, FaSave, FaArrowLeft, FaFacebookMessenger, FaKey, FaCheck, FaUpload } from "react-icons/fa";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { driversAPI, driversVehiclesAPI } from '../../api/apiClient';
import { useTranslation } from 'react-i18next';
import { formatNumber } from "../../utils/helpers";
import Swal from 'sweetalert2';
import PaginationComponent from '../common/PaginationComponent';
import LocationAutocomplete from '../LocationAutocomplete';

export default function DriverDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVehicleEditModal, setShowVehicleEditModal] = useState(false);
  const [showVehicleAddModal, setShowVehicleAddModal] = useState(false);
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const fromPage = location.state?.fromPage || 1;
  const userId = location.state?.userId;
  const search = location.state?.search || '';
  const fromPath = location.state?.fromPath || '/admin/drivers';
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });
  
  const [profileData, setProfileData] = useState({
    id: "",
    firstname: "",
    lastname: "",
    is_term: "",
    is_data_protection: "",
    is_transport_conditions: "",
    is_legal_notice: "",
    nicname: "",
    company_name: "",
    email: "",
    addressone: "",
    street_address: "",
    postal_code: ""
  });

  const [vehicleForm, setVehicleForm] = useState({
    driver_name: "",
    phone_number: "",
    vehicle_number: "",
    vehicle_type: "",
    max_weight: "",
    max_length: "",
    max_width: "",
    max_height: "",
  });

  const [editData, setEditData] = useState({
    firstname: "",
    lastname: "",
    nicname: "",
    company_name: "",
    email: "",
    addressone: "",
    street_address: "",
    postal_code: "",
    comercial_image: "",
    carrierinsurance_image: "",
    efu_license_image: "",
  });

  // إضافة حالة للملفات الجديدة
  const [newFiles, setNewFiles] = useState({
    comercial_image: [],
    carrierinsurance_image: [],
    efu_license_image: [],
  });
  const [newFileExpiryDates, setNewFileExpiryDates] = useState({
    comercial_image: [],
    carrierinsurance_image: [],
    efu_license_image: [],
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    password_confirmation: ''
  });

  const [vehicles, setVehicles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [updatingDocId, setUpdatingDocId] = useState(null);
  const [tempExpiryDates, setTempExpiryDates] = useState({});
  const isDeletedAccount =
    profileData?.is_deleted === 1 ||
    profileData?.is_deleted === true ||
    profileData?.is_deleted === "1" ||
    profileData?.is_deleted === "true";
  const deletionReason =
    profileData?.deleted_reason ||
    profileData?.deletion_reason ||
    profileData?.delete_reason;

  const handleDateChange = (fileId, newDate) => {
    setTempExpiryDates(prev => ({
      ...prev,
      [fileId]: newDate
    }));
  };

  // Fetch driver details
  const fetchDriverDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await driversAPI.show(id);
      
      if (response.success) {
        const driver = response.data.data;
        console.log(driver);

        setProfileData(driver);
        setEditData(driver);
        
        const documentTypes = [
          { key: 'comercial_image', label: t('comercial_image') },
          { key: 'carrierinsurance_image', label: t('carrierinsurance_image') },
          { key: 'efu_license_image', label: t('efu_license_image') },
        ];

        const documentsArray = [];
        documentTypes.forEach(({ key, label }) => {
          const document = driver[key];
          if (!document) return;

          const files = Array.isArray(document.files) ? document.files : [];
          if (files.length > 0) {
            files.forEach((file) => {
              documentsArray.push({
                id: file.id,
                document_id: document.id,
                name: label,
                document_type: key,
                file_url: file.url,
                file_name: file.name,
                expire_date: file.expire_date || '',
              });
            });
          } else {
            documentsArray.push({
              id: document.id,
              document_id: document.id,
              name: label,
              document_type: key,
              file_url: document.url,
              file_name: document.url?.split('/').pop(),
              expire_date: document.expire_date || '',
            });
          }
        });
        
        setDocuments(documentsArray);
      } else {
        setError(response.message || t('failed_to_fetch_driver'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_driver'));
      console.error('Error fetching driver details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch driver vehicles
  const fetchDriverVehicles = async (page = 1) => {
    try {
      setVehiclesLoading(true);
      const response = await driversVehiclesAPI.getByDriver(id, {
        page,
        per_page: pagination.per_page,
      });

      console.log(response);
      
      if (response.success) {
        setVehicles(response.data.data || []);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.data.length,
        });
      } else {
        setError(response.message || t('failed_to_fetch_vehicles'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_vehicles'));
      console.error('Error fetching vehicles:', err);
    } finally {
      setVehiclesLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchDriverDetails();
      fetchDriverVehicles();
    }
  }, [id]);

  const handleLocationChange = (e) => {
    setEditData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
      ...(e.lat && { [`${e.target.name}_lat`]: e.lat }),
      ...(e.lng && { [`${e.target.name}_lng`]: e.lng })
    }));
  };

  const handleVehicleChange = (e) => {
    setVehicleForm({ ...vehicleForm, [e.target.name]: e.target.value });
  };

  // دالة التعامل مع اختيار الملفات
  const handleFileChange = (e, fileType) => {
    const files = Array.from(e.target.files || []);
    setNewFiles(prev => ({
      ...prev,
      [fileType]: files
    }));
    setNewFileExpiryDates(prev => ({
      ...prev,
      [fileType]: files.map(() => '')
    }));
  };

  const handleNewFileExpiryDateChange = (fileType, index, value) => {
    setNewFileExpiryDates(prev => {
      const copy = [...(prev[fileType] || [])];
      copy[index] = value;
      return {
        ...prev,
        [fileType]: copy,
      };
    });
  };

  const getDocumentFiles = (documentType) => {
    const document = profileData?.[documentType];
    if (!document || !Array.isArray(document.files)) {
      return [];
    }
    return document.files;
  };

  const formatFileDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString();
  };

  const handleDeleteDocumentFile = async (fileId) => {
    if (!fileId) {
      Swal.fire({
        icon: 'warning',
        title: t('warning'),
        text: t('file_cannot_be_deleted'),
        timer: 2500,
        showConfirmButton: false
      });
      return;
    }

    const result = await Swal.fire({
      title: t('are_you_sure'),
      text: t('you_wont_be_able_to_revert_this'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('yes'),
      cancelButtonText: t('cancel')
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoading(true);
      const response = await driversAPI.deleteDocumentFile(id, fileId);

      if (!response.success) {
        throw new Error(response.message || t('failed_to_delete_file'));
      }

      await fetchDriverDetails();

      Swal.fire({
        icon: 'success',
        title: t('success'),
        text: t('file_deleted_successfully'),
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: err.message || t('failed_to_delete_file'),
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  const renderDocumentFiles = (documentType) => {
    const files = getDocumentFiles(documentType);

    if (!files.length) {
      return <div className="small text-muted mt-2">{t('no_files')}</div>;
    }

    return (
      <div className="table-responsive mt-2">
        <Table size="sm" bordered hover className="mb-0">
          <thead>
            <tr>
              <th style={{ width: '50%' }}>{t('file')}</th>
              <th style={{ width: '30%' }}>{t('expiry_date')}</th>
              <th style={{ width: '20%' }}>{t('action')}</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file) => (
              <tr key={`${documentType}-${file.id ?? file.name}`}>
                <td>
                  <a href={file.url} target="_blank" rel="noreferrer">{file.name}</a>
                </td>
                <td>{file.expire_date || '-'}</td>
                <td>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={!file.id}
                    onClick={() => handleDeleteDocumentFile(file.id)}
                  >
                    ×
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderSelectedFileExpiryInputs = (documentType) => {
    const files = newFiles[documentType] || [];
    if (!files.length) {
      return null;
    }

    return (
      <div className="mt-2">
        <Table size="sm" bordered hover className="mb-0">
          <thead>
            <tr>
              <th>{t('file')}</th>
              <th>{t('expiry_date')}</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file, index) => (
              <tr key={`${documentType}-new-${index}`}>
                <td>{file.name}</td>
                <td>
                  <Form.Control
                    type="date"
                    value={newFileExpiryDates[documentType]?.[index] || ''}
                    onChange={(e) => handleNewFileExpiryDateChange(documentType, index, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const handleSaveVehicle = async () => {
    try {
      setLoading(true);
      let response;
      
      if (selectedVehicleIndex !== null) {
        // Update existing vehicle
        const vehicleId = vehicles[selectedVehicleIndex].id;
        response = await driversVehiclesAPI.update(vehicleId, vehicleForm);
      } else {
        // Add new vehicle
        response = await driversVehiclesAPI.update(0, {
          ...vehicleForm,
          driver_id: id
        });
      }
      
      if (response.success) {
        setShowVehicleEditModal(false);
        setShowVehicleAddModal(false);
        fetchDriverVehicles();
        setVehicleForm({
          driver_name: "",
          phone_number: "",
          vehicle_number: "",
          vehicle_type: "",
          max_weight: "",
          max_length: "",
          max_width: "",
          max_height: ""
        });

        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t('vehicle_updated_successfully'),
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t('failed_to_save_vehicle'),
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t('failed_to_save_vehicle'),
        timer: 3000,
        showConfirmButton: false,
      });
      console.error('Error saving vehicle:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle vehicle deletion
  const confirmDelete = async () => {
    if (!selectedVehicle) return; 
    try {
      setLoading(true);
      const response = await driversVehiclesAPI.delete(selectedVehicle);
      
      if (response.success) {
        setShowDelete(false);
        setSelectedVehicle(null); 
        fetchDriverVehicles(pagination.current_page);
      } else {
        alert(response.message || t('failed_to_delete_vehicle'));
      }
    } catch (err) {
      setError(t('failed_to_delete_vehicle'));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = async () => {
    try {
      setLoading(true);

      const documentTypes = ['comercial_image', 'carrierinsurance_image', 'efu_license_image'];
      for (const documentType of documentTypes) {
        const selectedFiles = newFiles[documentType] || [];
        const expiryDates = newFileExpiryDates[documentType] || [];
        if (selectedFiles.length > 0 && expiryDates.some((date) => !date)) {
          throw new Error(t('all_files_must_have_expiry_date'));
        }
      }
      
      // إنشاء FormData
      const formData = new FormData();
      
      // إضافة البيانات النصية
      const textFields = {
        firstname: editData.firstname,
        lastname: editData.lastname,
        nicname: editData.nicname,
        company_name: editData.company_name,
        email: editData.email,
        addressone: editData.addressone,
        street_address: editData.street_address,
        postal_code: editData.postal_code,
      };
      
      Object.entries(textFields).forEach(([key, value]) => {
        if (value) {
          formData.append(key, value);
        }
      });
      
      // إضافة الملفات
      for (const file of newFiles.comercial_image) {
        formData.append('comercial_image[]', file);
      }
      for (const expireDate of (newFileExpiryDates.comercial_image || [])) {
        formData.append('comercial_image_expire_dates[]', expireDate);
      }
      for (const file of newFiles.carrierinsurance_image) {
        formData.append('carrierinsurance_image[]', file);
      }
      for (const expireDate of (newFileExpiryDates.carrierinsurance_image || [])) {
        formData.append('carrierinsurance_image_expire_dates[]', expireDate);
      }
      for (const file of newFiles.efu_license_image) {
        formData.append('efu_license_image[]', file);
      }
      for (const expireDate of (newFileExpiryDates.efu_license_image || [])) {
        formData.append('efu_license_image_expire_dates[]', expireDate);
      }
      
      console.log('Sending form data with files:', {
        textFields,
        files: newFiles
      });
      
      // استخدام دالة update الحالية
      const response = await driversAPI.update(id, formData);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t('user_updated_successfully'),
          timer: 3000,
          showConfirmButton: false,
        });
        setShowEditModal(false);
        // إعادة تعيين الملفات
        setNewFiles({
          comercial_image: [],
          carrierinsurance_image: [],
          efu_license_image: [],
        });
        setNewFileExpiryDates({
          comercial_image: [],
          carrierinsurance_image: [],
          efu_license_image: [],
        });
        // تحديث البيانات
        fetchDriverDetails();
      } else {
        throw new Error(response.message || t('failed_to_update_user'));
      }
    } catch (err) {
      console.error('Error updating user:', err);
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t('failed_to_update_user'),
        timer: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      if (!passwordData.password || passwordData.password.length < 8) {
        setPasswordError(t("password_must_be_at_least_8_characters"));
        return;
      }

      if (passwordData.password !== passwordData.password_confirmation) {
        setPasswordError(t("passwords_do_not_match"));
        return;
      }

      setLoading(true);
      const response = await driversAPI.update(id, {
        password: passwordData.password,
        password_confirmation: passwordData.password_confirmation
      });
      
      if (response.success) {
        setShowPasswordModal(false);
        setPasswordData({ password: '', password_confirmation: '' });
        setPasswordError("");
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t('password_updated_successfully'),
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t('failed_to_update_password'),
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t('failed_to_update_password'),
        timer: 3000,
        showConfirmButton: false,
      });
      console.error('Error updating password:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleUpdateDocumentExpiry = async (fileId) => {
    const expiryDate = tempExpiryDates[fileId];
    
    if (!expiryDate) {
      Swal.fire({
        icon: 'warning',
        title: t('warning'),
        text: t('please_select_date'),
        timer: 3000,
      });
      return;
    }

    try {
      setUpdatingDocId(fileId);
      
      const response = await driversAPI.updateDocumentExpiry(id, fileId, {
        expire_date: expiryDate
      });
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('document_updated_successfully'),
          timer: 3000,
          showConfirmButton: false,
        });
        
        // تحديث الحالة المحلية
        setDocuments(prev => prev.map(doc => 
          doc.id === fileId 
            ? { ...doc, expire_date: expiryDate }
            : doc
        ));
        
        // تنظيف التاريخ المؤقت
        setTempExpiryDates(prev => {
          const newTemp = { ...prev };
          delete newTemp[fileId];
          return newTemp;
        });
        
      } else {
        throw new Error(response.message || t('failed_to_update_document'));
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: err.message || t('failed_to_update_document'),
        timer: 3000,
      });
    } finally {
      setUpdatingDocId(null);
    }
  };

  const handleConfirmed = async () => {
    const result = await Swal.fire({
      title: t('are_you_sure'),
      text: t('confirm_user'),
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: 'rgba(51, 221, 57, 1)',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('confirm'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await driversAPI.confirm(id);
        
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: t('confirmed'),
            text: t('driver_confirmed_successfully'),
            timer: 3000,
            showConfirmButton: false
          });
          setProfileData(editData);
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: err.message || t('failed_to_confirm_driver'),
          timer: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    navigate(`${fromPath}?page=${fromPage}${search ? `&search=${encodeURIComponent(search)}` : ''}`, { 
      state: { 
        scrollToUser: userId 
      } 
    });
  };


  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="container-wrapper">
        {/* Header */}
        <div className="custom-header text-white p-4 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
          <div className="d-flex align-items-center">
            <Button 
              variant="light" 
              className="me-3"
              onClick={handleBack}
              title={t('back_to_list')}
            >
              <FaArrowLeft />
            </Button>
            <h2 className="mb-0">{profileData.firstname} {profileData.lastname}</h2>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {profileData.is_confirmed === 0 &&
              <Button 
                variant="info" 
                onClick={handleConfirmed}
                disabled={loading}
                size="sm"
              >
                <FaCheck className="me-1" /> {t("confirm_driver")}
              </Button>
            }
            <button
              className="btn btn-light"
              onClick={() => setShowPasswordModal(true)}
            >
              <FaKey className="me-1" />
              {t('change_password')}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowEditModal(true)}
            >
              <FaEdit className="me-1" />
              {t('edit_profile')}
            </button>
            <button
              className="btn btn-success"
              onClick={() => navigate(`/admin/driver-list/chat-driver/${profileData.id}`)}
            >
              <FaFacebookMessenger className="me-1" />
            </button>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {isDeletedAccount && (
          <Alert variant="warning" className="mt-3">
            <strong>{t("deleted_account")}:</strong>{" "}
            {deletionReason || t("no_deletion_reason")}
          </Alert>
        )}

        {/* Info Cards */}
        <div className="row mt-4">
          <div className="col-md-6 mb-3">
            <div className="card p-3 h-100">
              <p className="mb-1">
                <strong>{profileData.firstname} {profileData.lastname}</strong>
              </p>
              <p className="mb-1"><b>{t('last_name')}:</b> {profileData.nicname}</p>
              <p className="mb-1"><b>{t('company_name')}:</b> {profileData.company_name}</p>
              <p className="mb-1"><b>{t('Email')}:</b> {profileData.email}</p>
              <p className="mb-1"><b>{t('phone')}:</b> {profileData.telphone}</p>
              <p className="mb-1"><b>{t('website')}:</b> <a href={profileData.website} target="_blank" rel="noopener noreferrer">{profileData.website}</a></p>
              <p className="mb-1"><b>{t('VAT/Tax_No')}:</b> {profileData.tax_no}</p>
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="card p-3 h-100">
              <p className="mb-1"><b>{t('local_court')}:</b> {profileData.court}</p>
              <p className="mb-1">
                <b>{t('responsible_person')}</b> {profileData.responsible_person}
              </p>
              <p className="mb-1"><b>{t('street')}:</b> {profileData.street_address}</p>
              <p className="mb-1"><b>{t('addressone')}:</b> {profileData.addressone}</p>
              <p className="mb-1"><b>{t('postal_code')}:</b> {profileData.postal_code}</p>
              <p className="mb-1"><b>{t('driver_agreed_terms')}</b> {(profileData.is_terms ?? profileData.is_term) ? '✅ ' + t('yes') : '❌ ' + t('no')}</p>
              <p className="mb-1"><b>{t('driver_agreed_data_protections')}</b> {(profileData.is_data_protections ?? profileData.is_data_protection) ? '✅ ' + t('yes') : '❌ ' + t('no')}</p>
              <p className="mb-1"><b>{t('driver_agreed_transport_conditions')}</b> {(profileData.is_transport_conditions ?? profileData.is_transport_conditions) ? '✅ ' + t('yes') : '❌ ' + t('no')}</p>
              <p className="mb-1"><b>{t('driver_agreed_legal_notices')}</b> {(profileData.is_legal_notices ?? profileData.is_legal_notice) ? '✅ ' + t('yes') : '❌ ' + t('no')}</p>
            </div>
          </div>
          {/* Rating Section */}
        </div>

        {/* Rating Section as separate card */}
        {profileData.total_reviews > 0 && (
        <div className="card mt-4 p-3">
          <h5 className="fw-bold border-bottom pb-2 mb-3">⭐ {t('ratings_reviews')}</h5>
          <div className="row align-items-center">
            <div className="col-md-4 text-center">
              <div className="average-rating display-4 fw-bold text-warning mb-2">
                {profileData.average_rating || 0}
              </div>
              <div className="rating-stars mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`fs-4 ${star <= Math.round(profileData.average_rating || 0) ? 'text-warning' : 'text-muted'}`}
                  >
                    ★
                  </span>
                ))}
              </div>
              <div className="total-reviews small text-muted">
                {t('based_on')} {profileData.total_reviews || 0} {t('reviews')}
              </div>
            </div>

             <div className="col-md-8">
              <div className="rating-breakdown">
                {[5, 4, 3, 2, 1].map((rating) => {
                  // حساب عدد المراجعات لكل تقييم من بيانات rating array
                  const ratingCount = profileData.rating 
                    ? profileData.rating.filter(r => Math.round(r.rating) === rating).length
                    : 0;
                  
                  const percentage = (ratingCount / (profileData.total_reviews || 1)) * 100;
                  
                  return (
                    <div key={rating} className="rating-bar d-flex align-items-center mb-2">
                      <div className="rating-label me-2" style={{ width: '50px' }}>
                        <span className="text-warning">{rating} ★</span>
                      </div>
                      <div className="progress flex-grow-1 me-2" style={{ height: '8px' }}>
                        <div
                          className="progress-bar bg-warning"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="rating-count small text-muted" style={{ width: '30px' }}>
                        {ratingCount}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Documents Section */}
        {/* {documents.length > 0 && ( */}
          <div className="card mt-4 p-3">
            <h5 className="mb-3">📄 {t('documents')}</h5>
            <div className="table-responsive">
              <Table bordered hover>
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>{t('document_name')}</th>
                    <th>{t('file')}</th>
                    <th>{t('expiry_date')}</th>
                    <th>{t('status')}</th>
                    <th>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, index) => (
                    <tr key={`${doc.document_type}-${doc.id ?? index}`}>
                      <td>{index + 1}</td>
                      <td>{doc.name}</td>
                      <td>
                        <a href={doc.file_url} target="_blank" rel="noreferrer">
                          {doc.file_name || t('view')}
                        </a>
                      </td>
                      <td>
                        <div className="d-flex gap-2 align-items-center">
                          <Form.Control
                            type="date"
                            value={tempExpiryDates[doc.id] || doc.expire_date || ''}
                            onChange={(e) => handleDateChange(doc.id, e.target.value)}
                            disabled={updatingDocId === doc.id || !doc.id}
                          />
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleUpdateDocumentExpiry(doc.id)}
                            disabled={
                              !doc.id ||
                              updatingDocId === doc.id || 
                              !tempExpiryDates[doc.id] ||
                              tempExpiryDates[doc.id] === doc.expire_date
                            }
                          >
                            {updatingDocId === doc.id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              t('update')
                            )}
                          </Button>
                        </div>
                      </td>
                      <td>
                        <Badge bg={
                          doc.expire_date
                            ? (new Date(doc.expire_date) < new Date(new Date().toDateString()) ? 'danger' : 'success')
                            : 'secondary'
                        }>
                          {doc.expire_date
                            ? (new Date(doc.expire_date) < new Date(new Date().toDateString()) ? t('expired') : t('valid'))
                            : t('no_expiry_date')}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => window.open(doc.file_url, '_blank')}
                          className="me-2"
                        >
                          {t('view')}
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={!doc.id}
                          onClick={() => handleDeleteDocumentFile(doc.id)}
                        >
                          ×
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        {/* )} */}

        {/* Vehicles Table */}
        <div className="card mt-4 p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">🚗 {t('vehicles')}</h5>
            {/* <Button
              variant="success"
              onClick={() => {
                setVehicleForm({
                  driver_name: "",
                  phone_number: "",
                  vehicle_number: "",
                  vehicle_type: "",
                  max_weight: "",
                  max_length: "",
                  max_width: "",
                  max_height: ""
                });
                setSelectedVehicleIndex(null);
                setShowVehicleAddModal(true);
              }}
            >
              <FaPlus className="me-1" />
              {t('add_vehicle')}
            </Button> */}
          </div>

          {vehiclesLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">{t('loading_vehicles')}</p>
            </div>
          ) : vehicles.length === 0 ? (
            <Alert variant="info">{t('no_vehicles')}</Alert>
          ) : (
            <div className="table-responsive">
              <Table bordered hover className="align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>{t('driver_name')}</th>
                    <th>{t('phone')}</th>
                    <th>{t('vehicle_number')}</th>
                    <th>{t('vehicle_type')}</th>
                    <th>{t('max_weight')}</th>
                    <th>{t('dimensions')}</th>
                    <th>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((vehicle, index) => (
                    <tr key={vehicle.id}>
                      <td>{index + 1}</td>
                      <td>{vehicle.driver_name}</td>
                      <td>{vehicle.phone_number}</td>
                      <td>{vehicle.vehicle_number}</td>
                      <td>{vehicle.vehicle_type}</td>
                      <td>{formatNumber(vehicle.max_weight)} <small>Kg</small></td>
                      <td>{vehicle.max_length} x {vehicle.max_width} x {vehicle.max_height} <small>cm</small></td>
                      <td>
                        <Button
                          size="sm"
                          className="me-2"
                          onClick={() => {
                            setVehicleForm(vehicle);
                            setSelectedVehicleIndex(index);
                            setShowVehicleEditModal(true);
                          }}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setSelectedVehicle(vehicle.id);
                            setShowDelete(true);
                          }}
                          title={t('Delete')}
                        >
                          <FaTrashAlt />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>

        {pagination.total > 0 && (
        <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={fetchDriverVehicles}
            disabled={loading}
          />
        )}

        {/* Password Modal */}
        <Modal
          show={showPasswordModal}
          onHide={() => setShowPasswordModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('change_password')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t('new_password')}</Form.Label>
                <Form.Control
                  type="password"
                  value={passwordData.password}
                  onChange={(e) => {
                    setPasswordData({...passwordData, password: e.target.value});
                    setPasswordError("");
                  }}
                  placeholder={t('new_password')}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('confirm_password')}</Form.Label>
                <Form.Control
                  type="password"
                  value={passwordData.password_confirmation}
                  onChange={(e) => {
                    setPasswordData({...passwordData, password_confirmation: e.target.value});
                    setPasswordError("");
                  }}
                  placeholder={t('confirm_password')}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPasswordModal(false);
                setPasswordError("");
              }}
              disabled={loading}
            >
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={handlePasswordChange} disabled={loading}>
              {loading ? t('saving') : t('save')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Profile Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('edit_profile')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('firstname')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstname"
                      value={editData.firstname}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('lastname')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastname"
                      value={editData.lastname}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('nicname')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="nicname"
                      value={editData.nicname}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('company_name')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="company_name"
                      value={editData.company_name}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={editData.email}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('street_address')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="street_address"
                      value={editData.street_address}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>{t('addressone')}</Form.Label>
                <Form.Control
                  type="text"
                  name="addressone"
                  value={editData.addressone}
                  onChange={handleChange}
                />
              </Form.Group>
              
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3" controlId="formPostalCode">
                    <Form.Label>{t('postal_code')}</Form.Label>
                    <LocationAutocomplete
                      name="postal_code"
                      value={editData.postal_code}
                      onChange={handleLocationChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* إضافة حقول رفع الملفات */}
              <Row>
                <Col md={12}>
                  <h6 className="border-bottom pb-2 mb-3">{t('documents')}</h6>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('comercial_image')}</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      onChange={(e) => handleFileChange(e, 'comercial_image')}
                    />
                    {newFiles.comercial_image.length > 0 && (
                      <small className="text-success">
                        <FaUpload className="me-1" />
                        {t('file_selected')}: {newFiles.comercial_image.length}
                      </small>
                    )}
                    {renderSelectedFileExpiryInputs('comercial_image')}
                    {renderDocumentFiles('comercial_image')}
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('carrierinsurance_image')}</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      onChange={(e) => handleFileChange(e, 'carrierinsurance_image')}
                    />
                    {newFiles.carrierinsurance_image.length > 0 && (
                      <small className="text-success">
                        <FaUpload className="me-1" />
                        {t('file_selected')}: {newFiles.carrierinsurance_image.length}
                      </small>
                    )}
                    {renderSelectedFileExpiryInputs('carrierinsurance_image')}
                    {renderDocumentFiles('carrierinsurance_image')}
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('efu_license_image')}</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      onChange={(e) => handleFileChange(e, 'efu_license_image')}
                    />
                    {newFiles.efu_license_image.length > 0 && (
                      <small className="text-success">
                        <FaUpload className="me-1" />
                        {t('file_selected')}: {newFiles.efu_license_image.length}
                      </small>
                    )}
                    {renderSelectedFileExpiryInputs('efu_license_image')}
                    {renderDocumentFiles('efu_license_image')}
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={loading}>
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={handleProfileChange} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : <FaSave className="me-1" />}
              {t('save_changes')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Vehicle Edit/Add Modal */}
        <Modal
          show={showVehicleEditModal || showVehicleAddModal}
          onHide={() => {
            setShowVehicleEditModal(false);
            setShowVehicleAddModal(false);
          }}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedVehicleIndex !== null ? t('Edit') : t('Add')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('driver_name')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="driver_name"
                      value={vehicleForm.driver_name}
                      onChange={handleVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('phone')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="phone_number"
                      value={vehicleForm.phone_number}
                      onChange={handleVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('vehicle_number')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="vehicle_number"
                      value={vehicleForm.vehicle_number}
                      onChange={handleVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('vehicle_type')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="vehicle_type"
                      value={vehicleForm.vehicle_type}
                      onChange={handleVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('max_weight')} (Kg)</Form.Label>
                    <Form.Control
                      type="number"
                      name="max_weight"
                      value={vehicleForm.max_weight}
                      onChange={handleVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('max_length')} (cm)</Form.Label>
                    <Form.Control
                      type="number"
                      name="max_length"
                      value={vehicleForm.max_length}
                      onChange={handleVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('max_width')} (cm)</Form.Label>
                    <Form.Control
                      type="number"
                      name="max_width"
                      value={vehicleForm.max_width}
                      onChange={handleVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('max_height')} (cm)</Form.Label>
                    <Form.Control
                      type="number"
                      name="max_height"
                      value={vehicleForm.max_height}
                      onChange={handleVehicleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowVehicleEditModal(false);
                setShowVehicleAddModal(false);
              }}
            >
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={handleSaveVehicle}>
              {t('save')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDelete}
          onHide={() => setShowDelete(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('confirm_delete')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t('are_you_sure_delete_vehicle')}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDelete(false)}>
              {t('cancel')}
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              {t('delete')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}
